// A `question` event's decrypted content (crates/ocs-capture/src/domain/question.rs):
// `{"question_id", "question", "options", "deadline_seconds"?}`. Its id is also in the
// plaintext metadata, so a question can be matched to its event before it is decrypted.
import type { EventView } from "./model";

export interface AskedQuestion {
  questionId: string;
  text: string;
  options: string[];
}

export function parseQuestion(plaintext: string): AskedQuestion | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(plaintext);
  } catch {
    return undefined;
  }
  if (!parsed || typeof parsed !== "object") return undefined;
  const { question_id: questionId, question, options } = parsed as Record<string, unknown>;
  if (typeof questionId !== "string" || typeof question !== "string") return undefined;
  const choices = Array.isArray(options) ? options.filter((o): o is string => typeof o === "string") : [];
  return { questionId, text: question, options: choices };
}

/** The question id an event carries in its metadata, if it is a question. */
export function questionIdOf(event: EventView): string | undefined {
  if (event.type !== "question") return undefined;
  const meta = event.meta;
  if (meta && typeof meta === "object" && "question_id" in meta && typeof meta.question_id === "string") return meta.question_id;
  return event.content.kind === "plaintext" ? parseQuestion(event.content.text)?.questionId : undefined;
}

/** The decrypted question for an id, when this browser holds its event and key. */
export function findQuestion(events: EventView[], questionId: string): AskedQuestion | undefined {
  for (const e of events) {
    if (questionIdOf(e) !== questionId || e.content.kind !== "plaintext") continue;
    const asked = parseQuestion(e.content.text);
    if (asked) return asked;
  }
  return undefined;
}
